import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import { Check, MapPin, Briefcase, Sparkles, Send } from "lucide-react";
import ReactMarkdown from "react-markdown";

const EMPLOYMENT_TYPE_LABELS = {
  full_time: "Full-time",
  part_time: "Part-time",
  contract: "Contract",
  freelance: "Freelance",
  internship: "Internship",
};

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const c = currency || "EUR";
  if (min && max) return `${c} ${min.toLocaleString()}–${max.toLocaleString()}`;
  if (min) return `${c} ${min.toLocaleString()}+`;
  return `Up to ${c} ${max.toLocaleString()}`;
}

function locationLabel(item) {
  if (item.is_remote) return "Remote";
  const parts = [item.location_city, item.location_country].filter(Boolean);
  return parts.join(", ") || null;
}

const ENTRY_TYPE_LABELS = {
  experience: "Experience",
  education: "Education",
  certification: "Certification",
  project: "Project",
  volunteer: "Volunteer",
  achievement: "Achievement",
  publication: "Publication",
};

function formatDateRange(startedAt, endedAt, isCurrent) {
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
      : null;
  const start = fmt(startedAt);
  const end = isCurrent ? "Present" : fmt(endedAt);
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  if (end) return end;
  return null;
}

export default function PublicShareView() {
  const { shareToken } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState({});
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Candidate share";
    fetchShare();
  }, [shareToken]);

  const fetchShare = async () => {
    try {
      const { data } = await api.get(`/shares/${shareToken}`);
      setShare(data);
      // If feedback already submitted, lock the page
      if (data.feedback_submitted_at) {
        setSubmitted(true);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404)
        setError("This share link is invalid or has been removed.");
      else if (status === 410) setError("This share link has expired.");
      else setError("We couldn't load this share. Please check the link.");
    } finally {
      setLoading(false);
    }
  };

  const toggleHighlight = (applicationId) => {
    if (submitted) return;
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[applicationId]) delete next[applicationId];
      else next[applicationId] = true;
      return next;
    });
  };

  const selectedCount = Object.keys(selectedIds).length;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/shares/${shareToken}/feedback`, {
        selected_application_ids: Object.keys(selectedIds),
        comment: comment.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      alert(
        err.response?.data?.detail ||
          "Failed to submit feedback. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-brand-dark-bg p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold mb-3">Share unavailable</h1>
          <p className="text-gray-500 dark:text-gray-400">{error}</p>
          <p className="text-xs text-gray-400 mt-6">Powered by Joborigo</p>
        </div>
      </div>
    );
  }

  if (!share) return null;

  const positionLocation = share.is_remote
    ? "Remote"
    : [share.location_city, share.location_country].filter(Boolean).join(", ");
  const positionSalary = formatSalary(
    share.salary_min,
    share.salary_max,
    share.salary_currency,
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      {/* Header — agency branding */}
      <header className="bg-white dark:bg-brand-dark-surface border-b border-gray-200 dark:border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          {share.agency_logo_url ? (
            <img
              src={share.agency_logo_url}
              alt={share.agency_name}
              className="w-10 h-10 object-contain rounded"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-brand-violet rounded flex items-center justify-center text-white font-bold">
              {share.agency_name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-semibold">{share.agency_name}</p>
            {share.client_label && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Prepared for {share.client_label}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Talent Brief — position context */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-8">
          <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-2">
            Position
          </p>
          <h1 className="text-2xl font-bold mb-3">{share.position_title}</h1>

          <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-300 mb-4">
            {positionLocation && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {positionLocation}
              </span>
            )}
            {share.employment_type && (
              <span className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4" />
                {EMPLOYMENT_TYPE_LABELS[share.employment_type] ||
                  share.employment_type}
              </span>
            )}
            {positionSalary && (
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {positionSalary}
              </span>
            )}
          </div>

          {share.position_description && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{share.position_description}</ReactMarkdown>
            </div>
          )}

          {share.position_employer_message && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-brand-dark-bg rounded-md text-sm text-gray-600 dark:text-gray-300 italic">
              {share.position_employer_message}
            </div>
          )}
        </section>

        {/* Candidates header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">
              {share.items.length} candidate{share.items.length !== 1 && "s"}
            </h2>
            {!submitted && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Tap a candidate to reveal their identity and highlight your
                interest. Submit when you're done.
              </p>
            )}
          </div>
          {!submitted && selectedCount > 0 && (
            <span className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark">
              {selectedCount} highlighted
            </span>
          )}
        </div>

        {/* Candidate cards */}
        <div className="space-y-3 mb-8">
          {share.items.map((item) => {
            const isSelected = !!selectedIds[item.application_id];
            const cardLocation = locationLabel(item);
            const cardSalary = formatSalary(
              item.salary_min,
              item.salary_max,
              item.salary_currency,
            );

            return (
              <button
                key={item.application_id}
                onClick={() => toggleHighlight(item.application_id)}
                disabled={submitted}
                className={`w-full text-left p-5 rounded-xl border-2 transition relative ${
                  isSelected
                    ? "border-brand-violet dark:border-brand-violet-on-dark bg-brand-violet-tint dark:bg-brand-violet/10"
                    : "border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface hover:border-gray-300 dark:hover:border-white/20"
                } ${submitted ? "cursor-default" : "cursor-pointer"}`}
              >
                {item.is_top_pick && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-brand-violet text-white rounded-full">
                    <Sparkles className="w-3 h-3" />
                    Top pick
                  </span>
                )}

                <div className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition ${
                      isSelected
                        ? "bg-brand-violet border-brand-violet dark:border-brand-violet-on-dark text-white"
                        : "border-gray-300 dark:border-white/20"
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1">
                      {isSelected
                        ? item.candidate_full_name ||
                          `Candidate ${item.candidate_initial}`
                        : `Candidate ${item.candidate_initial}`}
                    </h3>
                    {!isSelected && (
                      <p className="text-xs text-gray-400 italic mb-1">
                        Tap to reveal identity and resume
                      </p>
                    )}
                    {item.job_title && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                        {item.job_title}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                      {cardLocation && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {cardLocation}
                        </span>
                      )}
                      {item.employment_type && (
                        <span>
                          {EMPLOYMENT_TYPE_LABELS[item.employment_type] ||
                            item.employment_type}
                        </span>
                      )}
                      {cardSalary && <span>{cardSalary}</span>}
                    </div>

                    {/* Expanded resume content — shown when highlighted */}
                    {isSelected && (
                      <div
                        className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.resume_summary && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                              Summary
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {item.resume_summary}
                            </p>
                          </div>
                        )}

                        {item.timeline_entries &&
                          item.timeline_entries.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
                                Background
                              </p>
                              <div className="space-y-3">
                                {item.timeline_entries.map((entry, idx) => {
                                  const dateRange = formatDateRange(
                                    entry.started_at,
                                    entry.ended_at,
                                    entry.is_current,
                                  );
                                  return (
                                    <div
                                      key={idx}
                                      className="text-sm border-l-2 border-brand-violet/30 dark:border-brand-violet-on-dark/30 pl-3"
                                    >
                                      <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
                                        <span className="font-medium">
                                          {entry.title}
                                        </span>
                                        {entry.organisation && (
                                          <span className="text-gray-500 dark:text-gray-400">
                                            · {entry.organisation}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                                        <span className="text-brand-violet dark:text-brand-violet-on-dark font-medium">
                                          {ENTRY_TYPE_LABELS[
                                            entry.entry_type
                                          ] || entry.entry_type}
                                        </span>
                                        {dateRange && (
                                          <span>· {dateRange}</span>
                                        )}
                                        {entry.location && (
                                          <span>· {entry.location}</span>
                                        )}
                                        {entry.issuer && (
                                          <span>· {entry.issuer}</span>
                                        )}
                                      </div>
                                      {entry.description && (
                                        <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap mt-1">
                                          {entry.description}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                        {!item.resume_summary &&
                          (!item.timeline_entries ||
                            item.timeline_entries.length === 0) && (
                            <p className="text-xs text-gray-400 italic">
                              This candidate hasn't published a full resume yet.
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Submit / submitted state */}
        {submitted ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-green-600 flex items-center justify-center">
              <Check className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-green-900 dark:text-green-200 mb-1">
              Feedback sent
            </p>
            <p className="text-sm text-green-700 dark:text-green-300">
              {share.agency_name} has been notified. Thanks for taking the time
              to review the candidates.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-200 dark:border-white/10 p-5">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
              Comments to {share.agency_name} (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Anything you'd like to share — questions, priorities, next steps..."
              rows={3}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-md text-sm resize-none"
            />

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-brand-violet text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? (
                "Submitting..."
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send feedback to {share.agency_name}
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 text-center mt-3">
              You can highlight zero candidates if none are a fit.{" "}
              {share.agency_name} will appreciate the honest feedback.
            </p>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-12">
          Powered by{" "}
          <a
            href="https://joborigo.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
          >
            Joborigo
          </a>
        </p>
      </main>
    </div>
  );
}
